import { useCallback, useEffect, useState } from "react";

function readStoredValue<T>(key: string, defaultValue: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
}

function changeEventName(key: string): string {
  return `local-pref-change:${key}`;
}

/**
 * Persists a small preference value to localStorage under `key`. Multiple
 * components calling this hook with the same key stay in sync within the
 * same tab via a custom event, since the native `storage` event only fires
 * in other tabs.
 */
export function useLocalPref<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readStoredValue(key, defaultValue));

  useEffect(() => {
    const handler = (event: Event): void => {
      setValue((event as CustomEvent<T>).detail);
    };
    window.addEventListener(changeEventName(key), handler);
    return () => window.removeEventListener(changeEventName(key), handler);
  }, [key]);

  const update = useCallback(
    (next: T) => {
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // ignore write failures (e.g. private browsing storage quota)
      }
      window.dispatchEvent(new CustomEvent(changeEventName(key), { detail: next }));
    },
    [key],
  );

  return [value, update];
}
